import { useState, type KeyboardEvent, type ReactNode } from 'react'
import { useWidth } from './useWidth'
import s from './Chart.module.css'

export interface Bar {
  label: string
  value: number
  /** A comparison value drawn beside it in grey, like the same hour last week. */
  compare?: number
}

/**
 * A bar chart in plain SVG. One series, or one plus a grey comparison. Hover or tap a bar for its numbers, or focus
 * the chart and use the arrow keys. `labelEvery` thins the axis labels when there are many bars.
 * `empty` shows instead when every value is zero or there are no bars.
 */
export function BarChart(props: {
  data: Bar[]
  seriesLabel: string
  compareLabel?: string
  format?: (v: number) => string
  height?: number
  labelEvery?: number
  empty: ReactNode
}) {
  const [hover, setHover] = useState<number | null>(null)
  const fmt = props.format ?? ((v: number) => String(v))
  const [ref, W] = useWidth<HTMLDivElement>()
  const H = props.height ?? 220
  const pad = { top: 8, right: 4, bottom: 22, left: 48 }
  const innerW = W - pad.left - pad.right
  const innerH = H - pad.top - pad.bottom
  const hasCompare = props.data.some((d) => d.compare !== undefined)
  const max = Math.max(1, ...props.data.flatMap((d) => [d.value, d.compare ?? 0]))
  const ticks = niceTicks(max)
  const top = ticks[ticks.length - 1]!
  const slot = innerW / Math.max(1, props.data.length)
  const gap = 2
  const barW = Math.max(2, (slot * 0.72 - (hasCompare ? gap : 0)) / (hasCompare ? 2 : 1))
  const y = (v: number) => pad.top + innerH - (v / top) * innerH
  // Fewer axis labels on narrow screens, so they never collide.
  const every = Math.max(props.labelEvery ?? 1, Math.ceil(props.data.length / Math.max(2, Math.floor(innerW / 64))))
  const active = hover === null ? null : props.data[hover]
  const onKey = (e: KeyboardEvent<SVGSVGElement>) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return
    e.preventDefault()
    const last = props.data.length - 1
    setHover((h) => (h === null ? 0 : Math.max(0, Math.min(last, h + (e.key === 'ArrowRight' ? 1 : -1)))))
  }
  if (props.data.length === 0 || props.data.every((d) => d.value === 0 && !d.compare)) return <>{props.empty}</>

  return (
    <div className={s.chart} ref={ref}>
      {hasCompare && (
        <div className={s.legend}>
          <span><i style={{ background: 'var(--chart-1)' }} />{props.seriesLabel}</span>
          <span><i style={{ background: 'var(--chart-ref)' }} />{props.compareLabel ?? 'Compare'}</span>
        </div>
      )}
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width={W}
        height={H}
        role="img"
        aria-label={active ? `${active.label}: ${fmt(active.value)}` : `${props.seriesLabel}. Use the arrow keys to read each bar.`}
        tabIndex={0}
        onKeyDown={onKey}
        onBlur={() => setHover(null)}
        onMouseLeave={() => setHover(null)}
      >
        {ticks.map((t) => (
          <g key={t}>
            <line className={s.gridLine} x1={pad.left} x2={W - pad.right} y1={y(t)} y2={y(t)} />
            <text className={s.axis} x={pad.left - 8} y={y(t) + 3.5} textAnchor="end">{fmt(t)}</text>
          </g>
        ))}
        {props.data.map((d, i) => {
          const x0 = pad.left + i * slot + (slot - (hasCompare ? barW * 2 + gap : barW)) / 2
          return (
            <g key={d.label} opacity={hover === null || hover === i ? 1 : 0.55}>
              {hasCompare && <path d={barPath(x0, y(d.compare ?? 0), barW, y(0))} fill="var(--chart-ref)" />}
              <path d={barPath(hasCompare ? x0 + barW + gap : x0, y(d.value), barW, y(0))} fill="var(--chart-1)" />
              {i % every === 0 && (
                <text className={s.axis} x={pad.left + i * slot + slot / 2} y={H - 6} textAnchor="middle">{d.label}</text>
              )}
              <rect
                x={pad.left + i * slot}
                y={pad.top}
                width={slot}
                height={innerH}
                fill="transparent"
                onMouseEnter={() => setHover(i)}
                onTouchStart={() => setHover(i)}
              />
            </g>
          )
        })}
      </svg>
      {active && hover !== null && (
        <div
          className={s.tooltip}
          style={{ left: `${((pad.left + hover * slot + slot / 2) / W) * 100}%`, top: `${(y(Math.max(active.value, active.compare ?? 0)) / H) * 100}%` }}
        >
          {active.label}
          <br />
          {props.seriesLabel}: {fmt(active.value)}
          {active.compare !== undefined && (
            <>
              <br />
              {props.compareLabel ?? 'Compare'}: {fmt(active.compare)}
            </>
          )}
        </div>
      )}
    </div>
  )
}

/** A bar with 4px rounded top corners, sitting on the baseline. */
function barPath(x: number, top: number, w: number, base: number): string {
  const h = base - top
  if (h <= 0) return ''
  const r = Math.min(4, w / 2, h)
  return `M${x},${base}V${top + r}Q${x},${top} ${x + r},${top}H${x + w - r}Q${x + w},${top} ${x + w},${top + r}V${base}Z`
}

/** 0 and up to four round steps that cover `max`. */
export function niceTicks(max: number): number[] {
  const raw = max / 4
  const mag = 10 ** Math.floor(Math.log10(raw))
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? 10 * mag
  const out = [0]
  while (out[out.length - 1]! < max) out.push(Math.round((out[out.length - 1]! + step) * 1e6) / 1e6)
  return out
}
